// Cold-start persistence for /brief/$id.
//
// W4-C: when a user lands on a contract detail page for the first time we
// surface a short "what you can do here" panel. The panel is dismissable; the
// dismissal sticks for the lifetime of the browser session, keyed by opp id
// so each new contract gets one bite at the apple.
//
// Storage: a JSON-serialized array of seen opp ids under
//   localStorage[STORAGE_KEY] = '["opp-a","opp-b",…]'
//
// We deliberately store an *array* on the wire rather than a `Set` because
// `JSON.stringify(new Set(…))` returns `{}` (the lossy default). The runtime
// shape is `Set<string>`; the wire shape is `string[]`. Keep the boundary
// thin and tested.
//
// The "v1" suffix gates a future schema bump (e.g. per-tenant scoping). When
// the key needs to change we ship "v2" and silently abandon the old key.

export const STORAGE_KEY = 'eid:brief:coldstart-seen:v1';

/** Read the persisted set of opp ids that have already dismissed the panel.
 *  Tolerant of malformed JSON, missing keys, and corrupted shapes — any
 *  parse failure resets the set to empty rather than throwing in render. */
export function readSeenSet(storage: Storage | null | undefined): Set<string> {
  if (!storage) return new Set();
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return new Set();
  }
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    const out = new Set<string>();
    for (const v of parsed) {
      if (typeof v === 'string' && v.length > 0) out.add(v);
    }
    return out;
  } catch {
    return new Set();
  }
}

/** Write the seen-set back to storage. Best-effort: a storage exception
 *  (quota, privacy mode) is swallowed — the next render will re-attempt. */
export function writeSeenSet(storage: Storage | null | undefined, seen: Set<string>): void {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seen)));
  } catch {
    /* best-effort */
  }
}

/** Pure check: should the cold-start panel render for this opp? */
export function shouldShowColdStart(storage: Storage | null | undefined, oppId: string): boolean {
  if (!oppId) return false;
  return !readSeenSet(storage).has(oppId);
}

/** Mark this opp as seen. Returns the updated set (useful for tests). */
export function markColdStartSeen(storage: Storage | null | undefined, oppId: string): Set<string> {
  const seen = readSeenSet(storage);
  if (!oppId) return seen;
  seen.add(oppId);
  writeSeenSet(storage, seen);
  return seen;
}
